import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  InputBase,
  Button,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemIcon,
  ListItemButton,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExpandLess,
  ExpandMore,
  PersonOutline as PersonOutlineIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Close as CloseIcon,
  PolicyOutlined as PolicyOutlinedIcon,
  PrivacyTipOutlined as PrivacyTipOutlinedIcon,
  AssignmentReturnOutlined as AssignmentReturnOutlinedIcon,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getProfileMenuItems } from "../Profile/ProfileMenuItems";
import config from "../../Config";
import AssetConfig from "../../Theme/Assest";
import PopUp from "../Pop-up/pop-up";

const COLORS = {
  white: "#FFFFFF",
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};


const PRIMARY_CATEGORY_NAMES = ["men", "women", "kid", "kids"];
const COLLECTION_KEY = "collection";
const APP_DOWNLOAD_URL = "https://play.google.com/store/apps/details?id=com.ytctechventures.hindustanwardrobe"; 

const isPrimaryCategory = (category) =>
  PRIMARY_CATEGORY_NAMES.includes(
    (category?.mainCategoryName || "").toString().trim().toLowerCase(),
  );

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: 40,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 0,
  backgroundColor: COLORS.softCream,
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: COLORS.lightGrey,
    backgroundColor: COLORS.white,
  },
  "&:focus-within": {
    borderColor: COLORS.black,
    backgroundColor: COLORS.white,
    boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
  },
  [theme.breakpoints.down("sm")]: {
    height: 42,
  },
}));

const SearchIconWrapper = styled("div")(() => ({
  position: "absolute",
  left: 0,
  top: 0,
  height: "100%",
  width: 42,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  color: COLORS.grey,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: "100%",
  color: COLORS.black,
  "& .MuiInputBase-input": {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px 9px 42px",
    fontSize: 12,
    letterSpacing: "0.02em",
    "&::placeholder": {
      color: COLORS.lightGrey,
      opacity: 1,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiInputBase-input": {
      fontSize: 13,
      padding: "10px 10px 10px 42px",
    },
  },
}));

const NavButton = styled(Button)(() => ({
  minWidth: "auto",
  padding: "9px 12px",
  color: COLORS.mutedBlack,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.14em",
  borderRadius: 0,
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "transparent",
    color: COLORS.gold,
  },
  "& .MuiButton-endIcon": {
    marginLeft: 3,
  },
}));

const CategoryImage = ({ src, title }) => {
  const [imageError, setImageError] = useState(false);

  const firstLetter = title?.trim()?.charAt(0)?.toUpperCase() || "H";

  if (!src || imageError) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 250,
          background:
            "linear-gradient(145deg, #ece8e1 0%, #d9d2c7 50%, #bcb3a6 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 15,
            border: "1px solid rgba(255,255,255,0.65)",
          }}
        />

        <Typography
          sx={{
            fontFamily: "Georgia, serif",
            fontSize: 70,
            fontWeight: 300,
            color: "rgba(255,255,255,0.72)",
          }}
        >
          {firstLetter}
        </Typography>

        <Typography
          sx={{
            position: "absolute",
            bottom: 18,
            left: 20,
            right: 20,
            color: COLORS.white,
            fontSize: 9,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={title || "Collection"}
      onError={() => setImageError(true)}
      sx={{
        width: "100%",
        height: 250,
        display: "block",
        objectFit: "cover",
        objectPosition: "center",
      }}
    />
  );
};

const DropdownMenu = ({
  anchorEl,
  open,
  onClose,
  items,
  title,
  imageUrl,
  onCategoryClick,
}) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disableScrollLock
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          mt: 1.3,
          width: {
            xs: "calc(100vw - 24px)",
            md: "calc(100vw - 70px)",
          },
          maxWidth: 1320,
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 0,
          backgroundColor: COLORS.white,
          boxShadow: "0 22px 60px rgba(0,0,0,0.12)",
          "& .MuiMenu-list": {
            padding: 0,
          },
        },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            py: { xs: 3, md: 4 },
            px: { xs: 1, md: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 9,
                  color: COLORS.gold,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  mb: 0.8,
                }}
              >
                Explore the collection
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: 22, md: 28 },
                  fontWeight: 400,
                  color: COLORS.black,
                }}
              >
                {title}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 0,
                color: COLORS.grey,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: COLORS.cream,
                  color: COLORS.black,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          <Divider
            sx={{
              mb: 3,
              borderColor: COLORS.border,
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 3fr) minmax(220px, 1fr)",
              },
              gap: { xs: 3, md: 4 },
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: { xs: 2, md: 3 },
                }}
              >
                {items?.length > 0 ? (
                  items.map((item, index) => {
                    const categoryName =
                      item?.categoryName ||
                      item?.subcategoryName ||
                      item?.name ||
                      "Collection";

                    const subcategories =
                      item?.subcategories || item?.subcategoryList || [];

                    return (
                      <Box
                        key={
                          item?.categoryId ||
                          item?.subcategoryId ||
                          item?.id ||
                          item?._id ||
                          index
                        }
                        sx={{
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          onClick={() => onCategoryClick(item, title)}
                          sx={{
                            color: COLORS.black,
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            cursor: "pointer",
                            mb: 1.3,
                            transition: "all 0.2s ease",
                            wordBreak: "break-word",
                            "&:hover": {
                              color: COLORS.gold,
                              transform: "translateX(3px)",
                            },
                          }}
                        >
                          {categoryName}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.6,
                          }}
                        >
                          {Array.isArray(subcategories) &&
                            subcategories.map((subcategory, subIndex) => {
                              const name =
                                subcategory?.subcategoryName ||
                                subcategory?.name;

                              if (!name) {
                                return null;
                              }

                              return (
                                <Typography
                                  key={
                                    subcategory?.subcategoryId ||
                                    subcategory?.id ||
                                    subcategory?._id ||
                                    subIndex
                                  }
                                  onClick={() =>
                                    onCategoryClick(subcategory, title)
                                  }
                                  sx={{
                                    color: COLORS.grey,
                                    fontSize: 11,
                                    lineHeight: 1.5,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    wordBreak: "break-word",
                                    "&:hover": {
                                      color: COLORS.black,
                                      transform: "translateX(3px)",
                                    },
                                  }}
                                >
                                  {name}
                                </Typography>
                              );
                            })}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    sx={{
                      color: COLORS.grey,
                      fontSize: 13,
                      py: 3,
                    }}
                  >
                    No categories available.
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                backgroundColor: COLORS.cream,
              }}
            >
              <CategoryImage src={imageUrl} title={title} />

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.48), transparent 55%)",
                  pointerEvents: "none",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  bottom: 18,
                  left: 18,
                  right: 18,
                  color: COLORS.white,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 8,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Hindustan Wardrobe
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 21,
                  }}
                >
                  Dress the moment.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Menu>
  );
};

// COLLECTION dropdown: main category -> categories -> subcategories
const CollectionDropdownMenu = ({
  anchorEl,
  open,
  onClose,
  collections,
  categories,
  onCategoryClick,
}) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disableScrollLock
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{
        elevation: 0,
        sx: {
          mt: 1.3,
          width: { xs: "calc(100vw - 24px)", md: "calc(100vw - 70px)" },
          maxWidth: 1320,
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 0,
          backgroundColor: COLORS.white,
          boxShadow: "0 22px 60px rgba(0,0,0,0.12)",
          "& .MuiMenu-list": { padding: 0 },
        },
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 9,
                  color: COLORS.gold,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  mb: 0.8,
                }}
              >
                Explore the collection
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: 22, md: 28 },
                  color: COLORS.black,
                }}
              >
                Collections
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 0,
                color: COLORS.grey,
                "&:hover": {
                  backgroundColor: COLORS.cream,
                  color: COLORS.black,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3, borderColor: COLORS.border }} />

          {collections.map((main, mainIndex) => {
            const items = categories[main.mainCategoryName];
            const isLoading = items === undefined;
            const isLast = mainIndex === collections.length - 1;

            return (
              <Box
                key={main.mainCategoryId}
                sx={{
                  mb: isLast ? 0 : 4,
                  pb: isLast ? 0 : 4,
                  borderBottom: isLast ? "none" : `1px solid ${COLORS.border}`,
                }}
              >
                {/* Main category (e.g. Navratri) */}
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 20,
                    color: COLORS.gold,
                    mb: 2,
                  }}
                >
                  {main.mainCategoryName}
                </Typography>

                {isLoading ? (
                  <Typography sx={{ color: COLORS.lightGrey, fontSize: 12 }}>
                    Loading categories...
                  </Typography>
                ) : items.length === 0 ? (
                  <Typography sx={{ color: COLORS.grey, fontSize: 12 }}>
                    No categories available.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(5, minmax(0, 1fr))",
                      },
                      gap: { xs: 2, md: 3 },
                    }}
                  >
                    {items.map((item, index) => {
                      const categoryName =
                        item?.categoryName ||
                        item?.subcategoryName ||
                        item?.name ||
                        "Collection";
                      const subcategories =
                        item?.subcategories || item?.subcategoryList || [];

                      return (
                        <Box
                          key={
                            item?.categoryId || item?.id || item?._id || index
                          }
                          sx={{ minWidth: 0 }}
                        >
                          {/* Category */}
                          <Typography
                            onClick={() =>
                              onCategoryClick(item, main.mainCategoryName)
                            }
                            sx={{
                              color: COLORS.black,
                              fontSize: 12,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              cursor: "pointer",
                              mb: 1.3,
                              transition: "all 0.2s ease",
                              wordBreak: "break-word",
                              "&:hover": {
                                color: COLORS.gold,
                                transform: "translateX(3px)",
                              },
                            }}
                          >
                            {categoryName}
                          </Typography>

                          {/* Subcategories */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.6,
                            }}
                          >
                            {Array.isArray(subcategories) &&
                              subcategories.map((sub, subIndex) => {
                                const name = sub?.subcategoryName || sub?.name;
                                if (!name) return null;

                                return (
                                  <Typography
                                    key={
                                      sub?.subcategoryId ||
                                      sub?.id ||
                                      sub?._id ||
                                      subIndex
                                    }
                                    onClick={() =>
                                      onCategoryClick(
                                        sub,
                                        main.mainCategoryName,
                                      )
                                    }
                                    sx={{
                                      color: COLORS.grey,
                                      fontSize: 11,
                                      lineHeight: 1.5,
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                      wordBreak: "break-word",
                                      "&:hover": {
                                        color: COLORS.black,
                                        transform: "translateX(3px)",
                                      },
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                );
                              })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Container>
    </Menu>
  );
};

export default function Navbar() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width:900px)");

  const [searchQuery, setSearchQuery] = useState("");

  const [userId, setUserId] = useState(
    () =>
      sessionStorage.getItem("userId") ||
      sessionStorage.getItem("user-id") ||
      null,
  );

  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState(null);
  const [mobileCollectionOpen, setMobileCollectionOpen] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [mobileListingsOpen, setMobileListingsOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);

  const primaryCategories = useMemo(
    () => mainCategories.filter(isPrimaryCategory),
    [mainCategories],
  );
  const collectionCategories = useMemo(
    () => mainCategories.filter((category) => !isPrimaryCategory(category)),
    [mainCategories],
  );

  const profileMenuItems = getProfileMenuItems({
    navigate,
  });

  const bookingItem = profileMenuItems.find((i) =>
    /booking/i.test(`${i.key} ${i.label}`),
  );
  const accountItems = profileMenuItems.filter((i) => i !== bookingItem);

  useEffect(() => {
    const syncUser = () => {
      setUserId(
        sessionStorage.getItem("userId") ||
          sessionStorage.getItem("user-id") ||
          null,
      );
    };

    window.addEventListener("storage", syncUser);
    window.addEventListener("authchange", syncUser);

    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("authchange", syncUser);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchMainCategories = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/${config.getMainCategories}`,
        );
        if (!mounted) return;
        const data = response?.data?.MainCategory;
        setMainCategories(Array.isArray(data) ? data : []);
      } catch {
        if (mounted) {
          setMainCategories([]);
        }
      }
    };

    fetchMainCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const fetchCategories = async (mainCategoryName) => {
    if (!mainCategoryName) return;
    const categoryKey = mainCategoryName.toString().trim();
    if (!categoryKey) return;
    if (Object.prototype.hasOwnProperty.call(categories, categoryKey)) {
      return;
    }

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/${config.getCategoryAndSubCategory}`,
        {
          headers: {
            "main-category": categoryKey,
          },
        },
      );

      const categoryData = Array.isArray(response?.data?.categories)
        ? response.data.categories
        : [];

      setCategories((previous) => ({
        ...previous,
        [categoryKey]: categoryData,
      }));
    } catch {
      setCategories((previous) => ({
        ...previous,
        [categoryKey]: [],
      }));
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = () => {
    const query = searchQuery.trim();
    if (query) {
      navigate(`/AllProduct?search=${encodeURIComponent(query)}`);
    } else {
      navigate("/AllProduct");
    }

    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    handleDropdownClose();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDropdownOpen = (event, category) => {
    if (!category) return;
    setDropdownAnchorEl(event.currentTarget);
    setOpenDropdown(category.mainCategoryId);
    fetchCategories(category.mainCategoryName);
  };

  const handleCollectionOpen = (event) => {
    setDropdownAnchorEl(event.currentTarget);
    setOpenDropdown(COLLECTION_KEY);
    collectionCategories.forEach((category) =>
      fetchCategories(category.mainCategoryName),
    );
  };

  const handleDropdownClose = () => {
    setDropdownAnchorEl(null);
    setOpenDropdown(null);
  };

  const handleCategoryClick = (category, mainCategoryName) => {
    if (!mainCategoryName || !category) return;

    const mainName = mainCategoryName.toString().trim();

    const subName =
      category?.subcategoryName ||
      category?.name ||
      category?.categoryName ||
      "";

    const categoryId =
      category?.subcategoryId ||
      category?.categoryId ||
      category?.id ||
      category?._id;

    if (!mainName || !subName || !categoryId) {
      return;
    }

    const mainCatSlug = mainName.replace(/\s+/g, "-").toLowerCase();
    const subCatSlug = subName.replace(/\s+/g, "-").toLowerCase();
    navigate(`/category/${mainCatSlug}/${subCatSlug}/${categoryId}`);

    handleDropdownClose();
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goHome = () => {
    navigate("/");
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    handleDropdownClose();
    setUserMenuAnchorEl(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goWishlist = () => {
    navigate("/wishlist");
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    setUserMenuAnchorEl(null);
  };

  const goLogin = () => {
    navigate("/Signin");
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    setUserMenuAnchorEl(null);
  };

  const goRegister = () => {
    navigate("/signup");
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    setUserMenuAnchorEl(null);
  };

  const handleRedirect = (path) => {
    if (!path) return;

    navigate(path);
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);
    setUserMenuAnchorEl(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSignOut = () => {
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("user-id");
    sessionStorage.removeItem("newToken");

    setUserId(null);
    setUserMenuAnchorEl(null);
    setMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
    setMobileCollectionOpen(null);

    window.dispatchEvent(new Event("authchange"));

    navigate("/Signin");
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen((previous) => !previous);
  };

  const handleMobileSubMenuToggle = (id) => {
    setMobileSubMenuOpen((previous) => (previous === id ? null : id));

    if (id === COLLECTION_KEY) {
      collectionCategories.forEach((category) =>
        fetchCategories(category.mainCategoryName),
      );
      return;
    }
    const category = mainCategories.find((item) => item.mainCategoryId === id);
    if (category) fetchCategories(category.mainCategoryName);
  };

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
      setMobileSubMenuOpen(null);
      setMobileCollectionOpen(null);
    }
  }, [isDesktop]);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const listingOptions = [
    {
      label: "Product Listing",
      onClick: () => {
        setUserMenuAnchorEl(null);
        setMobileMenuOpen(false);
        setAppDialogOpen(true);
      },
    },
    {
      label: "Booking Requests",
      onClick: () => handleRedirect(bookingItem?.path),
    },
  ];

  const renderMobileCategoryList = (mainCategory) => {
    const items = categories[mainCategory.mainCategoryName];

    if (items === undefined) {
      return (
        <Typography
          sx={{ px: 2, py: 1, fontSize: 10, color: COLORS.lightGrey }}
        >
          Loading categories...
        </Typography>
      );
    }

    if (items.length === 0) {
      return (
        <Typography
          sx={{ px: 2, py: 1, fontSize: 10, color: COLORS.lightGrey }}
        >
          No categories available.
        </Typography>
      );
    }

    return items.map((category, categoryIndex) => {
      const subcategories =
        category?.subcategories || category?.subcategoryList || [];
      const categoryName = category?.categoryName || category?.name;

      return (
        <React.Fragment
          key={
            category?.categoryId ||
            category?.id ||
            category?._id ||
            categoryIndex
          }
        >
          {categoryName && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() =>
                  handleCategoryClick(category, mainCategory.mainCategoryName)
                }
                sx={{ py: 0.7, pl: 2 }}
              >
                <ListItemText
                  primary={categoryName}
                  primaryTypographyProps={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: COLORS.mutedBlack,
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}

          {Array.isArray(subcategories) &&
            subcategories.map((sub, subIndex) => {
              const name = sub?.subcategoryName || sub?.name;

              if (!name) return null;
              return (
                <ListItem
                  disablePadding
                  key={sub?.subcategoryId || sub?.id || sub?._id || subIndex}
                >
                  <ListItemButton
                    onClick={() =>
                      handleCategoryClick(sub, mainCategory.mainCategoryName)
                    }
                    sx={{ py: 0.6, pl: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <Box
                        sx={{
                          width: 3,
                          height: 3,
                          borderRadius: "50%",
                          backgroundColor: COLORS.goldLight,
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{
                        fontSize: 10,
                        color: COLORS.grey,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </React.Fragment>
      );
    });
  };

  const renderMobileToggleRow = ({ label, isOpen, onClick, small }) => (
    <ListItem disablePadding>
      <ListItemButton
        onClick={onClick}
        sx={{
          minHeight: small ? 40 : 46,
          "&:hover": { backgroundColor: COLORS.softCream },
        }}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          <ArrowForwardIosIcon
            sx={{
              fontSize: 11,
              color: COLORS.gold,
              transform: isOpen ? "rotate(90deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </ListItemIcon>

        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: small ? 10.5 : 11,
            fontWeight: 600,
            letterSpacing: "0.08em",
          }}
        />

        {isOpen ? (
          <ExpandLess sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18 }} />
        )}
      </ListItemButton>
    </ListItem>
  );

  const mobileNestedListSx = {ml: 2, mb: 1,borderLeft: `1px solid ${COLORS.border}`,};

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.97)",
          color: COLORS.black,
          borderBottom: `1px solid ${COLORS.border}`,
          backdropFilter: "blur(14px)",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 72 },
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              py: { xs: 1, md: 0 },
            }}
          >
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{
                    color: COLORS.black,
                    width: 40,
                    height: 40,
                  }}
                >
                  <MenuIcon />
                </IconButton>

                <Link
                  to="/"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={AssetConfig.logoWhite}
                    alt="Hindustan Wardrobe"
                    sx={{
                      width: 70,
                      height: 70,
                      objectFit: "contain",
                    }}
                  />
                </Link>
              </Box>

              {userId && (
                <IconButton
                  onClick={goWishlist}
                  sx={{
                    color: COLORS.black,
                    width: 40,
                    height: 40,
                  }}
                >
                  <FavoriteBorderIcon sx={{ fontSize: 21 }} />
                </IconButton>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                width: "100%",
                mt: 1,
              }}
            >
              <Search>
                <SearchIconWrapper>
                  <SearchIcon sx={{ fontSize: 19 }} />
                </SearchIconWrapper>

                <StyledInputBase
                  placeholder="Search for products"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  inputProps={{
                    "aria-label": "search products",
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearchSubmit();
                    }
                  }}
                />
              </Search>
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                width: "100%",
                minHeight: 72,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: { md: 120, lg: 150 },
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Link
                  to="/"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={AssetConfig.logoWhite}
                    alt="Hindustan Wardrobe"
                    sx={{
                      width: 90,
                      height: 90,
                      objectFit: "contain",
                    }}
                  />
                </Link>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <NavButton
                  onClick={goHome}
                  sx={{
                    color: COLORS.gold,
                  }}
                >
                  HOME
                </NavButton>

                {primaryCategories.map((category) => (
                  <NavButton
                    key={category.mainCategoryId}
                    onClick={(event) => handleDropdownOpen(event, category)}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 15 }} />}
                  >
                    {category.mainCategoryName}
                  </NavButton>
                ))}

                {collectionCategories.length > 0 && (
                  <NavButton
                    onClick={handleCollectionOpen}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 15 }} />}
                  >
                    COLLECTION
                  </NavButton>
                )}
              </Box>

              <Box
                sx={{
                  width: {
                    md: 200,
                    lg: 280,
                    xl: 330,
                  },
                  flexShrink: 1,
                }}
              >
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </SearchIconWrapper>

                  <StyledInputBase
                    placeholder="Search for products"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    inputProps={{
                      "aria-label": "search products",
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSearchSubmit();
                      }
                    }}
                  />
                </Search>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{
                    color: COLORS.mutedBlack,
                    borderRadius: 0,
                    px: 1,
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 21 }} />

                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 17,
                      color: COLORS.grey,
                    }}
                  />
                </IconButton>

                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1,
                      minWidth: 205,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 0,
                      boxShadow: "0 16px 40px rgba(0,0,0,0.10)",
                      "& .MuiMenuItem-root": {
                        fontSize: 11,
                        letterSpacing: "0.06em",
                        py: 1.4,
                        px: 2,
                      },
                    },
                  }}
                >
                  {userId ? (
                    <>
                      {accountItems.map((item) => (
                        <MenuItem
                          key={item.key}
                          onClick={() => {
                            handleUserMenuClose();
                            handleRedirect(item.path);
                          }}
                        >
                          {item.label.toUpperCase()}
                        </MenuItem>
                      ))}

                      <Typography
                        sx={{
                          px: 2,
                          pt: 1.2,
                          pb: 0.5,
                          fontSize: 9,
                          letterSpacing: "0.2em",
                          color: COLORS.gold,
                        }}
                      >
                        MY LISTINGS
                      </Typography>

                      {listingOptions.map((o) => (
                        <MenuItem
                          key={o.label}
                          onClick={() => {
                            handleUserMenuClose();
                            o.onClick();
                          }}
                          sx={{ pl: 3 }}
                        >
                          {o.label.toUpperCase()}
                        </MenuItem>
                      ))}

                      <Divider />

                      <MenuItem onClick={handleSignOut}>SIGN OUT</MenuItem>
                    </>
                  ) : (
                    <>
                      <MenuItem
                        onClick={() => {
                          handleUserMenuClose();
                          goRegister();
                        }}
                      >
                        CREATE ACCOUNT
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleUserMenuClose();
                          goLogin();
                        }}
                      >
                        SIGN IN
                      </MenuItem>
                    </>
                  )}
                </Menu>

                {userId && (
                  <IconButton
                    onClick={goWishlist}
                    sx={{
                      color: COLORS.mutedBlack,
                    }}
                  >
                    <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Toolbar>
        </Container>

        {primaryCategories.map((category) => (
          <DropdownMenu
            key={category.mainCategoryId}
            anchorEl={dropdownAnchorEl}
            open={openDropdown === category.mainCategoryId}
            onClose={handleDropdownClose}
            items={categories[category.mainCategoryName] || []}
            title={category.mainCategoryName}
            imageUrl={category.mainCategoryIcon}
            onCategoryClick={handleCategoryClick}
          />
        ))}

        {collectionCategories.length > 0 && (
          <CollectionDropdownMenu
            anchorEl={dropdownAnchorEl}
            open={openDropdown === COLLECTION_KEY}
            onClose={handleDropdownClose}
            collections={collectionCategories}
            categories={categories}
            onCategoryClick={handleCategoryClick}
          />
        )}
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{
          sx: {
            width: {
              xs: "86vw",
              sm: 340,
            },
            maxWidth: 360,
            backgroundColor: COLORS.white,
          },
        }}
      >
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={AssetConfig.logoWhite}
                alt="Hindustan Wardrobe"
                sx={{
                  width: 100,
                  height: 100,
                  objectFit: "contain",
                  mr: 1.2,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 17,
                    color: COLORS.black,
                    lineHeight: 1,
                  }}
                >
                  Hindustan
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 17,
                    fontStyle: "italic",
                    color: COLORS.gold,
                    lineHeight: 1.2,
                  }}
                >
                  Wardrobe.
                </Typography>
              </Box>
            </Link>

            <IconButton
              onClick={handleMobileMenuToggle}
              size="small"
              sx={{
                color: COLORS.black,
              }}
            >
              <CloseIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>

          <Divider />

          <List
            disablePadding
            sx={{
              px: 1,
              py: 1,
            }}
          >
            <ListItem disablePadding>
              <ListItemButton
                onClick={goHome}
                sx={{
                  minHeight: 46,
                  "&:hover": {
                    backgroundColor: COLORS.softCream,
                  },
                }}
              >
                <ListItemText
                  primary="HOME"
                  primaryTypographyProps={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                  }}
                />
              </ListItemButton>
            </ListItem>

            {primaryCategories.map((mainCategory) => {
              const isOpen = mobileSubMenuOpen === mainCategory.mainCategoryId;

              return (
                <React.Fragment key={mainCategory.mainCategoryId}>
                  {renderMobileToggleRow({
                    label: mainCategory.mainCategoryName,
                    isOpen,
                    onClick: () =>
                      handleMobileSubMenuToggle(mainCategory.mainCategoryId),
                  })}

                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List disablePadding sx={mobileNestedListSx}>
                      {renderMobileCategoryList(mainCategory)}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}

            {collectionCategories.length > 0 && (
              <>
                {renderMobileToggleRow({
                  label: "COLLECTION",
                  isOpen: mobileSubMenuOpen === COLLECTION_KEY,
                  onClick: () => handleMobileSubMenuToggle(COLLECTION_KEY),
                })}

                <Collapse
                  in={mobileSubMenuOpen === COLLECTION_KEY}
                  timeout="auto"
                  unmountOnExit
                >
                  <List disablePadding sx={mobileNestedListSx}>
                    {collectionCategories.map((mainCategory) => {
                      const isOpen =
                        mobileCollectionOpen === mainCategory.mainCategoryId;

                      return (
                        <React.Fragment key={mainCategory.mainCategoryId}>
                          {renderMobileToggleRow({
                            label: mainCategory.mainCategoryName,
                            isOpen,
                            small: true,
                            onClick: () =>
                              setMobileCollectionOpen((previous) =>
                                previous === mainCategory.mainCategoryId
                                  ? null
                                  : mainCategory.mainCategoryId,
                              ),
                          })}

                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <List disablePadding sx={mobileNestedListSx}>
                              {renderMobileCategoryList(mainCategory)}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            )}

            <Divider
              sx={{
                my: 1,
                borderColor: COLORS.border,
              }}
            />

            {userId ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() =>
                      setMobileSubMenuOpen((previous) =>
                        previous === "profile" ? null : "profile",
                      )
                    }
                    sx={{
                      minHeight: 46,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                      }}
                    >
                      <PersonOutlineIcon
                        sx={{
                          fontSize: 19,
                          color: COLORS.gold,
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary="MY ACCOUNT"
                      primaryTypographyProps={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                      }}
                    />

                    {mobileSubMenuOpen === "profile" ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>

                <Collapse
                  in={mobileSubMenuOpen === "profile"}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    disablePadding
                    sx={{
                      ml: 3,
                      borderLeft: `1px solid ${COLORS.border}`,
                    }}
                  >
                    {accountItems.map((item) => (
                      <ListItem disablePadding key={item.key}>
                        <ListItemButton
                          onClick={() => handleRedirect(item.path)}
                          sx={{
                            pl: 2,
                          }}
                        >
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: 10,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}

                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => setMobileListingsOpen((p) => !p)}
                        sx={{ pl: 2 }}
                      >
                        <ListItemText
                          primary="My Listings"
                          primaryTypographyProps={{ fontSize: 10 }}
                        />
                        {mobileListingsOpen ? (
                          <ExpandLess sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16 }} />
                        )}
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={mobileListingsOpen} timeout="auto" unmountOnExit>
                      <List
                        disablePadding
                        sx={{ ml: 2, borderLeft: `1px solid ${COLORS.border}` }}
                      >
                        {listingOptions.map((o) => (
                          <ListItem disablePadding key={o.label}>
                            <ListItemButton onClick={o.onClick} sx={{ pl: 2 }}>
                              <ListItemText
                                primary={o.label}
                                primaryTypographyProps={{
                                  fontSize: 10,
                                  color: COLORS.grey,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </List>
                </Collapse>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleSignOut}
                    sx={{
                      minHeight: 46,
                    }}
                  >
                    <ListItemText
                      primary="SIGN OUT"
                      primaryTypographyProps={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#8C4D4D",
                        letterSpacing: "0.08em",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() =>
                      setMobileSubMenuOpen((previous) =>
                        previous === "account" ? null : "account",
                      )
                    }
                    sx={{
                      minHeight: 46,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                      }}
                    >
                      <PersonOutlineIcon
                        sx={{
                          fontSize: 19,
                          color: COLORS.gold,
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary="ACCOUNT"
                      primaryTypographyProps={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                      }}
                    />

                    {mobileSubMenuOpen === "account" ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>

                <Collapse
                  in={mobileSubMenuOpen === "account"}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    disablePadding
                    sx={{
                      ml: 3,
                      borderLeft: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={goRegister}
                        sx={{
                          pl: 2,
                        }}
                      >
                        <ListItemText
                          primary="Sign Up"
                          primaryTypographyProps={{
                            fontSize: 10,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={goLogin}
                        sx={{
                          pl: 2,
                        }}
                      >
                        <ListItemText
                          primary="Sign In"
                          primaryTypographyProps={{
                            fontSize: 10,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>
              </>
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={() =>
                  setMobileSubMenuOpen((prev) =>
                    prev === "policies" ? null : "policies",
                  )
                }
                sx={{ minHeight: 46 }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <PolicyOutlinedIcon sx={{ fontSize: 19, color: COLORS.gold }} />
                </ListItemIcon>
                <ListItemText
                  primary="POLICIES"
                  primaryTypographyProps={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                  }}
                />
                {mobileSubMenuOpen === "policies" ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>

            <Collapse
              in={mobileSubMenuOpen === "policies"}
              timeout="auto"
              unmountOnExit
            >
              <List
                disablePadding
                sx={{ ml: 3, borderLeft: `1px solid ${COLORS.border}` }}
              >
                {[
                  ["Privacy Policy", "/privacy/1", PrivacyTipOutlinedIcon],
                  [
                    "Return & Refund Policy",
                    "/ReturnRefundPolicy/1",
                    AssignmentReturnOutlinedIcon,
                  ],
                ].map(([label, path, Icon]) => (
                  <ListItem disablePadding key={path}>
                    <ListItemButton
                      onClick={() => handleRedirect(path)}
                      sx={{ pl: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Icon sx={{ fontSize: 16, color: COLORS.goldLight }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{ fontSize: 10 }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>

          <Box
            sx={{
              mt: "auto",
              p: 2.5,
              borderTop: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.softCream,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 17,
                mb: 0.5,
              }}
            >
              Rent. Wear. Return.
            </Typography>

            <Typography
              sx={{
                fontSize: 9,
                color: COLORS.grey,
                lineHeight: 1.6,
              }}
            >
              Fashion for every occasion, without the commitment.
            </Typography>

            <Chip
              label="HINDUSTAN WARDROBE"
              size="small"
              sx={{
                mt: 1.5,
                height: 23,
                borderRadius: 0,
                backgroundColor: "#E9E2D8",
                fontSize: 7,
                letterSpacing: "0.15em",
              }}
            />
          </Box>
        </Box>
      </Drawer>

      <PopUp
        open={appDialogOpen}
        setOpen={setAppDialogOpen}
        title="Edit products in the app"
        description="To add or edit your product listings, download the Hindustan Wardrobe app."
        primaryText="Download app"
        secondaryText="Close"
        onPrimary={() => {
          window.open(APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
          setAppDialogOpen(false);
        }}
      />
    </>
  );
}